/**
 * Alert banner shown during patient creation when potential duplicate
 * patients are detected based on first name, last name, and date of birth.
 * Mirrors the duplicate warning pattern used in ReferralUploadButton.
 * @agentic connections
 */

// Components
import { Alert, Anchor, Group, Loader, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconUserSearch } from "@tabler/icons-react";

// Types
import type { DuplicatePatientMatch } from "@/hooks/domain/usePatientDuplicateCheck";

// Utils
import { parseFhirInstantForDisplay } from "@benchmark/ui/utils";

/**
 * Props for the duplicate patient alert.
 * @agentic connections
 */
type DuplicatePatientAlertProps = {
  duplicates: DuplicatePatientMatch[];
  isChecking: boolean;
  onViewPatient: (patientId: string) => void;
};

/**
 * Formats date of birth for display.
 * @agentic connections
 */
const formatDob = (dob?: string): string => {
  if (!dob) return "Unknown";
  try {
    return parseFhirInstantForDisplay(dob).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dob;
  }
};

/**
 * Alert banner for potential duplicate patients during creation.
 * @agentic connections
 */
export const DuplicatePatientAlert = ({
  duplicates,
  isChecking,
  onViewPatient,
}: DuplicatePatientAlertProps) => {
  if (duplicates.length === 0 && !isChecking) return null;

  if (isChecking && duplicates.length === 0) {
    return (
      <Alert
        icon={<Loader size={16} />}
        color="gray"
        mb="md"
        title="Checking for existing members..."
      />
    );
  }

  return (
    <Alert
      icon={<IconAlertTriangle size={16} />}
      color="yellow"
      title="Possible Duplicate Member"
      mb="md"
    >
      <Stack gap="sm">
        <Text size="sm">
          A member with similar information already exists. Please review before
          continuing.
        </Text>

        {duplicates.map((match) => (
          <Alert
            key={match.patientId}
            icon={<IconUserSearch size={16} />}
            color="gray"
            p="xs"
          >
            <Group justify="space-between" align="center">
              <Stack gap={2}>
                <Text size="sm" fw={500}>
                  {match.displayName}
                </Text>
                <Text size="xs" c="dimmed">
                  DOB: {formatDob(match.dateOfBirth)}
                </Text>
              </Stack>
              <Anchor size="sm" onClick={() => onViewPatient(match.patientId)}>
                View Member
              </Anchor>
            </Group>
          </Alert>
        ))}
      </Stack>
    </Alert>
  );
};
